import { Modal, Box, Grid, Typography, Divider, Button, useMediaQuery, useTheme } from '@mui/material';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendAnalyticsEvent } from '../../../utils/googleAnalytics';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { alertByPrefecture, alertLevelType } from '../types';
import { LegendBadges } from '../index';

const useAlertDetailModal = (
  selectedAlert: alertByPrefecture | null,
  latestUpdated: string,
  onRefresh: () => Promise<void>,
  isRefreshing: boolean,
  refreshKey: number,
) => {
  const [alertModalShow, setAlertModalShow] = useState<boolean>(false);
  return {
    setAlertModalShow,
    AlertModal: (
      <AlertModal
        show={alertModalShow}
        setAlertModalShow={setAlertModalShow}
        selectedAlert={selectedAlert}
        latestUpdated={latestUpdated}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        refreshKey={refreshKey}
      />
    ),
  };
};

type Props = {
  show: boolean;
  setAlertModalShow: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAlert: alertByPrefecture | null;
  latestUpdated: string;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
  refreshKey: number;
};

const AlertModal: FC<Props> = ({
  show,
  setAlertModalShow,
  selectedAlert,
  latestUpdated,
  onRefresh,
  isRefreshing,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const detailRows = buildDetailRows(selectedAlert, t('pages.jmaAlerts.cleared'));

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '96vw' : 'min(920px, 92vw)',
    maxHeight: isMobile ? '86vh' : '80vh',
    bgcolor: 'background.paper',
    border: '1px solid #d7dee8',
    borderRadius: isMobile ? '8px' : '10px',
    boxShadow: 24,
    p: isMobile ? 1.3 : 3,
    overflowY: 'auto',
  };

  const handleRefreshClick = async () => {
    sendAnalyticsEvent('jma_alert_refresh', {
      action: 'refresh',
      source: 'detail',
    });
    await onRefresh();
  };

  return (
    <>
      <Modal open={show} onClose={() => {
        // GA4: 気象警報詳細を閉じた操作を計測
        sendAnalyticsEvent('jma_alert_detail', { action: 'close' });
        setAlertModalShow(false);
      }}>
        <Box sx={style}>
          <Box sx={{ mb: isMobile ? 0.8 : 1.1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <Typography sx={{ color: '#4a5568', width: '18px', height: '18px', fontSize: isMobile ? '10pt' : '12pt' }}>{`▼`}</Typography>
              <Typography sx={{ fontSize: '13pt', lineHeight: 1.2 }}>{t('pages.jmaAlerts.title')}</Typography>
              <Typography sx={{ fontSize: isMobile ? '7pt' : '8.5pt', color: '#4a5568', marginTop: '2px' }}>
                ※ {t('pages.jmaAlerts.currentlyPaused')}
              </Typography>
            </div>

            <div style={{ display: 'flex', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap', marginTop: '8px' }}>
              <LegendBadges />
            </div>

            <div
              style={{
                width: '320px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: isMobile ? '6px' : '10px',
                alignItems: 'center',
                marginTop: isMobile ? '8px' : '10px',
              }}
            >
              <Typography sx={{ fontSize: isMobile ? '8pt' : '10pt', color: '#1a202c' }}>
                {formatLastUpdated(latestUpdated)}
              </Typography>
              <Typography sx={{ fontSize: isMobile ? '8pt' : '10pt', color: '#1a202c' }}>{t('pages.jmaAlerts.publisher')}</Typography>
              <Button
                onClick={handleRefreshClick}
                disabled={isRefreshing || !selectedAlert}
                sx={{
                  minWidth: 'auto',
                  p: 0,
                  color: '#1a202c',
                  fontSize: '16pt',
                  lineHeight: 1,
                }}
              >
                {isRefreshing ? '...' : <AutorenewIcon fontSize="inherit" sx={{ transform: 'rotate(90deg)' }} />}
              </Button>
            </div>
          </Box>

          <Typography sx={{ fontSize: '16pt', color: '#1a202c', mb: isMobile ? 0.8 : 1.2, mt: isMobile ? 0.7 : 1.4 }}>
            {selectedAlert?.prefecture || t('pages.jmaAlerts.prefectureFallback')}
          </Typography>

          <Grid container sx={{ px: isMobile ? 0.1 : 0.8, pb: isMobile ? 0.5 : 0.9, color: '#2d3748', fontWeight: 700 }}>
            <Grid item xs={2.2}></Grid>
            <Grid item xs={4.9}>
              <Typography sx={{ pl: 4, fontSize: '14pt', fontWeight: 'bold' }}>{t('pages.jmaAlerts.heavyRain')}</Typography>
            </Grid>
            <Grid item xs={4.9}>
              <Typography sx={{ pl: 4, fontSize: '14pt', fontWeight: 'bold' }}>{t('pages.jmaAlerts.flood')}</Typography>
            </Grid>
          </Grid>
          <Divider />

          {detailRows.map((row, idx: number) => {
              return (
                <Box key={idx}>
                  <Grid container style={{ marginTop: isMobile ? '7px' : '9px', marginBottom: isMobile ? '7px' : '9px' }}>
                    <Grid item xs={2.2}>
                      <Typography sx={{ color: '#1a202c', fontSize: '10pt' }}>{row.cityName}</Typography>
                    </Grid>
                    <Grid item xs={4.9}>
                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '4px' : '6px' }}>
                        {row.heavyRain.length > 0 &&
                          row.heavyRain.map((alert, index: number) => (
                            alert.level === 0 ? (
                              <Typography key={`${alert.label}-${index}`} sx={{ color: '#4a5568', fontSize: '12pt' }}>{alert.label}</Typography>
                            ) : (
                              <SingleAlertBadge label={alert.label} level={alert.level} key={`${alert.label}-${index}`} />
                            )
                          ))}
                        {row.heavyRain.length === 0 && (
                          <Typography sx={{ color: '#4a5568', fontSize: '12pt' }}>{t('pages.jmaAlerts.cleared')}</Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={4.9}>
                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '4px' : '6px' }}>
                        {row.flood.length > 0 &&
                          row.flood.map((alert, index: number) => (
                            alert.level === 0 || alert.isCleared ? (
                              <Typography key={`${alert.label}-${index}`} sx={{ color: '#4a5568', fontSize: '12pt' }}>{alert.label}</Typography>
                            ) : (
                              <SingleAlertBadge label={alert.label} level={alert.level} key={`${alert.label}-${index}`} />
                            )
                          ))}
                        {row.flood.length === 0 && (
                          <Typography sx={{ color: '#4a5568', fontSize: '12pt' }}>{t('pages.jmaAlerts.cleared')}</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                  <Divider />
                </Box>
              );
            })}
        </Box>
      </Modal>
    </>
  );
};

export default useAlertDetailModal;

type SquareProps = {
  label: string;
  level: Exclude<alertLevelType, 0>;
};

const SingleAlertBadge: FC<SquareProps> = ({ label, level }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorMap = {
    2: { backgroundColor: '#ffca1a', color: '#1a202c' },
    3: { backgroundColor: '#f40e1a', color: '#ffffff' },
    4: { backgroundColor: '#ad1bb9', color: '#ffffff' },
    5: { backgroundColor: '#2d2a36', color: '#ffffff' },
  } as const;
  return (
    <Box
      style={{
        maxWidth: '100%',
        backgroundColor: colorMap[level].backgroundColor,
        color: colorMap[level].color,
        padding: '0px 6px',
        borderRadius: '2px',
        fontSize: '12pt',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};

const LegendBlock = ({ title, level }: { title: string; level: 2 | 3 | 4 | 5 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const colorMap = {
    2: { backgroundColor: '#ffca1a', color: '#1a202c' },
    3: { backgroundColor: '#f40e1a', color: '#ffffff' },
    4: { backgroundColor: '#ad1bb9', color: '#ffffff' },
    5: { backgroundColor: '#2d2a36', color: '#ffffff' },
  } as const;
  return (
    <Box
      sx={{
        px: 1.1,
        py: isMobile ? 0.45 : 0.6,
        borderRadius: '2px',
        minWidth: isMobile ? '54px' : '66px',
        textAlign: 'center',
        fontWeight: 700,
        ...colorMap[level],
      }}
    >
      <Typography sx={{ fontSize: isMobile ? '6.8pt' : '8pt', lineHeight: 1.05 }}>{t('pages.jmaAlerts.level', { level })}</Typography>
      <Typography sx={{ fontSize: isMobile ? '9.5pt' : '12pt', lineHeight: 1.1 }}>{title}</Typography>
    </Box>
  );
};

type DetailAlert = {
  label: string;
  level: alertLevelType;
  isCleared?: boolean;
};

type DetailRow = {
  cityName: string;
  heavyRain: DetailAlert[];
  flood: DetailAlert[];
};

const buildDetailRows = (selectedAlert: alertByPrefecture | null, clearedLabel: string): DetailRow[] => {
  if (!selectedAlert) {
    return [];
  }

  const rows: Record<string, DetailRow> = {};
  selectedAlert.heavy_rain_cities.forEach((city) => {
    rows[city.code] = rows[city.code] || { cityName: city.name, heavyRain: [], flood: [] };
    if (city.level > 0) {
      rows[city.code].heavyRain.push({ label: city.kind, level: city.level });
    }
  });

  selectedAlert.flood_cities.forEach((city) => {
    rows[city.city_code] = rows[city.city_code] || { cityName: city.city_name, heavyRain: [], flood: [] };
    city.rivers.forEach((river) => {
      rows[city.city_code].flood.push({
        label: river.is_cleared || river.level === 0 ? clearedLabel : river.kind,
        level: river.level,
        isCleared: river.is_cleared,
      });
    });
  });

  return Object.values(rows);
};

const formatLastUpdated = (value: string) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const yyyy = date.getFullYear();
  const mm = `${date.getMonth() + 1}`.padStart(2, '0');
  const dd = `${date.getDate()}`.padStart(2, '0');
  const hh = `${date.getHours()}`.padStart(2, '0');
  const min = `${date.getMinutes()}`.padStart(2, '0');
  return `${yyyy}/${mm}/${dd} ${hh}:${min}`;
};
